/**
 * Canonical Deepgram STT language list — derived from
 * https://developers.deepgram.com/docs/models-languages-overview
 * (verified 2026-05-04). Keep in sync with backend/app/services/stt_languages.py
 * (parity test: backend/tests/unit/test_stt_languages_parity.py).
 *
 * `multi` enables Nova-3 multilingual code-switching across the 10 core
 * languages: en, es, fr, de, hi, ru, pt, ja, it, nl.
 */

export type STTLanguageGroup = 'auto' | 'common' | 'all'

export interface STTLanguage {
  /** BCP-47 code or 'multi' */
  code: string
  /** English display name */
  name: string
  /** Native-script display name */
  nativeName: string
  /** Whether Deepgram's `multi` mode covers this language */
  multiSupported: boolean
  /** Display grouping for the picker */
  group: STTLanguageGroup
}

/** The 10 core language base codes covered by Deepgram `multi` mode. */
export const MULTI_CORE_LANGUAGE_CODES: ReadonlySet<string> = new Set([
  'en', 'es', 'fr', 'de', 'hi', 'ru', 'pt', 'ja', 'it', 'nl',
])

export const STT_LANGUAGES: readonly STTLanguage[] = [
  // Auto-detect
  { code: 'multi', name: 'Auto-detect (10 languages)', nativeName: 'Auto', multiSupported: true, group: 'auto' },

  // Common — multi-supported core 10 (base codes)
  { code: 'en',    name: 'English',     nativeName: 'English',     multiSupported: true, group: 'common' },
  { code: 'es',    name: 'Spanish',     nativeName: 'Español',     multiSupported: true, group: 'common' },
  { code: 'fr',    name: 'French',      nativeName: 'Français',    multiSupported: true, group: 'common' },
  { code: 'de',    name: 'German',      nativeName: 'Deutsch',     multiSupported: true, group: 'common' },
  { code: 'hi',    name: 'Hindi',       nativeName: 'हिन्दी',         multiSupported: true, group: 'common' },
  { code: 'ru',    name: 'Russian',     nativeName: 'Русский',     multiSupported: true, group: 'common' },
  { code: 'pt',    name: 'Portuguese',  nativeName: 'Português',   multiSupported: true, group: 'common' },
  { code: 'ja',    name: 'Japanese',    nativeName: '日本語',       multiSupported: true, group: 'common' },
  { code: 'it',    name: 'Italian',     nativeName: 'Italiano',    multiSupported: true, group: 'common' },
  { code: 'nl',    name: 'Dutch',       nativeName: 'Nederlands',  multiSupported: true, group: 'common' },

  // Regional variants of the multi-supported languages
  { code: 'en-US', name: 'English (US)',          nativeName: 'English (US)',          multiSupported: true, group: 'all' },
  { code: 'en-GB', name: 'English (UK)',          nativeName: 'English (UK)',          multiSupported: true, group: 'all' },
  { code: 'en-AU', name: 'English (Australia)',   nativeName: 'English (Australia)',   multiSupported: true, group: 'all' },
  { code: 'en-IN', name: 'English (India)',       nativeName: 'English (India)',       multiSupported: true, group: 'all' },
  { code: 'en-NZ', name: 'English (New Zealand)', nativeName: 'English (NZ)',          multiSupported: true, group: 'all' },
  { code: 'es-419', name: 'Spanish (Latin America)', nativeName: 'Español (LatAm)',    multiSupported: true, group: 'all' },
  { code: 'fr-CA', name: 'French (Canada)',       nativeName: 'Français (Canada)',     multiSupported: true, group: 'all' },
  { code: 'de-CH', name: 'German (Switzerland)',  nativeName: 'Deutsch (Schweiz)',     multiSupported: true, group: 'all' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)',   nativeName: 'Português (Brasil)',    multiSupported: true, group: 'all' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)', nativeName: 'Português (Portugal)',  multiSupported: true, group: 'all' },
  { code: 'hi-Latn', name: 'Hindi (Latin script)', nativeName: 'Hindi (Latin)',        multiSupported: true, group: 'all' },
  { code: 'nl-BE', name: 'Dutch (Belgium / Flemish)', nativeName: 'Nederlands (België)', multiSupported: true, group: 'all' },

  // Other Deepgram-supported languages (Nova-3) — NOT multi-supported
  { code: 'ar',    name: 'Arabic',           nativeName: 'العربية',  multiSupported: false, group: 'all' },
  { code: 'zh',    name: 'Chinese',          nativeName: '中文',      multiSupported: false, group: 'all' },
  { code: 'zh-CN', name: 'Chinese (Mainland)', nativeName: '简体中文', multiSupported: false, group: 'all' },
  { code: 'zh-TW', name: 'Chinese (Taiwan)', nativeName: '繁體中文',  multiSupported: false, group: 'all' },
  { code: 'zh-HK', name: 'Chinese (Hong Kong / Cantonese)', nativeName: '粵語', multiSupported: false, group: 'all' },
  { code: 'ko',    name: 'Korean',           nativeName: '한국어',     multiSupported: false, group: 'all' },
  { code: 'ko-KR', name: 'Korean (South Korea)', nativeName: '한국어',  multiSupported: false, group: 'all' },
  { code: 'tr',    name: 'Turkish',          nativeName: 'Türkçe',    multiSupported: false, group: 'all' },
  { code: 'vi',    name: 'Vietnamese',       nativeName: 'Tiếng Việt', multiSupported: false, group: 'all' },
  { code: 'th',    name: 'Thai',             nativeName: 'ไทย',        multiSupported: false, group: 'all' },
  { code: 'th-TH', name: 'Thai (Thailand)',  nativeName: 'ไทย',        multiSupported: false, group: 'all' },
  { code: 'id',    name: 'Indonesian',       nativeName: 'Bahasa Indonesia', multiSupported: false, group: 'all' },
  { code: 'pl',    name: 'Polish',           nativeName: 'Polski',     multiSupported: false, group: 'all' },
  { code: 'cs',    name: 'Czech',            nativeName: 'Čeština',    multiSupported: false, group: 'all' },
  { code: 'sv',    name: 'Swedish',          nativeName: 'Svenska',    multiSupported: false, group: 'all' },
  { code: 'sv-SE', name: 'Swedish (Sweden)', nativeName: 'Svenska',    multiSupported: false, group: 'all' },
  { code: 'el',    name: 'Greek',            nativeName: 'Ελληνικά',   multiSupported: false, group: 'all' },
  { code: 'hu',    name: 'Hungarian',        nativeName: 'Magyar',     multiSupported: false, group: 'all' },
  { code: 'ro',    name: 'Romanian',         nativeName: 'Română',     multiSupported: false, group: 'all' },
  { code: 'uk',    name: 'Ukrainian',        nativeName: 'Українська', multiSupported: false, group: 'all' },
  { code: 'da',    name: 'Danish',           nativeName: 'Dansk',      multiSupported: false, group: 'all' },
  { code: 'fi',    name: 'Finnish',          nativeName: 'Suomi',      multiSupported: false, group: 'all' },
  { code: 'no',    name: 'Norwegian',        nativeName: 'Norsk',      multiSupported: false, group: 'all' },
  { code: 'sk',    name: 'Slovak',           nativeName: 'Slovenčina', multiSupported: false, group: 'all' },
  { code: 'bg',    name: 'Bulgarian',        nativeName: 'Български',  multiSupported: false, group: 'all' },
  { code: 'hr',    name: 'Croatian',         nativeName: 'Hrvatski',   multiSupported: false, group: 'all' },
  { code: 'lt',    name: 'Lithuanian',       nativeName: 'Lietuvių',   multiSupported: false, group: 'all' },
  { code: 'lv',    name: 'Latvian',          nativeName: 'Latviešu',   multiSupported: false, group: 'all' },
  { code: 'et',    name: 'Estonian',         nativeName: 'Eesti',      multiSupported: false, group: 'all' },
  { code: 'he',    name: 'Hebrew',           nativeName: 'עברית',       multiSupported: false, group: 'all' },
  { code: 'ms',    name: 'Malay',            nativeName: 'Bahasa Melayu', multiSupported: false, group: 'all' },
  { code: 'tl',    name: 'Tagalog',          nativeName: 'Tagalog',    multiSupported: false, group: 'all' },
  { code: 'ca',    name: 'Catalan',          nativeName: 'Català',     multiSupported: false, group: 'all' },
  { code: 'fa',    name: 'Persian',          nativeName: 'فارسی',       multiSupported: false, group: 'all' },
  { code: 'bn',    name: 'Bengali',          nativeName: 'বাংলা',         multiSupported: false, group: 'all' },
  { code: 'ta',    name: 'Tamil',            nativeName: 'தமிழ்',         multiSupported: false, group: 'all' },
  { code: 'te',    name: 'Telugu',           nativeName: 'తెలుగు',        multiSupported: false, group: 'all' },
  { code: 'mr',    name: 'Marathi',          nativeName: 'मराठी',         multiSupported: false, group: 'all' },
  { code: 'gu',    name: 'Gujarati',         nativeName: 'ગુજરાતી',        multiSupported: false, group: 'all' },
  { code: 'gu-IN', name: 'Gujarati (India)', nativeName: 'ગુજરાતી',       multiSupported: false, group: 'all' },
  { code: 'kn',    name: 'Kannada',          nativeName: 'ಕನ್ನಡ',          multiSupported: false, group: 'all' },
  { code: 'ur',    name: 'Urdu',             nativeName: 'اردو',         multiSupported: false, group: 'all' },
] as const

export const SUPPORTED_STT_LANGUAGE_CODES: ReadonlySet<string> =
  new Set(STT_LANGUAGES.map((l) => l.code))

export const SYSTEM_DEFAULT_STT_LANGUAGE = 'multi'
